import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../utils/axiosInstance';
import usePageTitle from '../hooks/usePageTitle';
import './RunProfiles.css';

const RunProfiles = () => {
  usePageTitle('Run Profiles');
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [availableItems, setAvailableItems] = useState({ pipelines: [], flows: [] });
  const [newProfile, setNewProfile] = useState({
    name: '',
    description: '',
    stop_on_failure: false,
    parallel_execution: false,
  });
  const navigate = useNavigate();

  useEffect(() => {
    fetchProfiles();
    fetchAvailableItems();
  }, []);

  const fetchProfiles = async () => {
    try {
      const response = await axiosInstance.get('/etl/run-profiles/');
      setProfiles(response.data);
      setLoading(false);
    } catch (err) {
      setError('Failed to load run profiles');
      setLoading(false);
    }
  };

  const fetchAvailableItems = async () => {
    try {
      const response = await axiosInstance.get('/etl/run-profiles/available_items/');
      setAvailableItems(response.data);
    } catch (err) {
      console.error('Failed to load available items:', err);
    }
  };

  const handleCreateProfile = async (e) => {
    e.preventDefault();
    try {
      const response = await axiosInstance.post('/etl/run-profiles/', newProfile);
      setProfiles([...profiles, response.data]);
      setShowCreateModal(false);
      setNewProfile({ name: '', description: '', stop_on_failure: false, parallel_execution: false });
      // Navigate to the new profile's detail page
      navigate(`/run-profiles/${response.data.id}`);
    } catch (err) {
      setError(err.response?.data?.name?.[0] || 'Failed to create profile');
    }
  };

  const handleDeleteProfile = async (profileId) => {
    if (!confirm('Are you sure you want to delete this run profile?')) return;
    try {
      await axiosInstance.delete(`/etl/run-profiles/${profileId}/`);
      setProfiles(profiles.filter(p => p.id !== profileId));
    } catch (err) {
      setError('Failed to delete profile');
    }
  };

  const handleTriggerProfile = async (profileId) => {
    try {
      await axiosInstance.post(`/etl/run-profiles/${profileId}/trigger/`);
      fetchProfiles(); // Refresh to show updated status
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to trigger profile');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      active: 'badge-success',
      inactive: 'badge-warning',
      archived: 'badge-secondary',
    };
    return statusClasses[status] || 'badge-secondary';
  };

  if (loading) {
    return (
      <div className="run-profiles-container">
        <div className="loading">Loading run profiles...</div>
      </div>
    );
  }

  return (
    <div className="run-profiles-container">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1>Run Profiles</h1>
          <p className="page-subtitle">Create and manage collections of flows and pipelines for batch execution</p>
        </div>
        <button onClick={() => setShowCreateModal(true)} className="btn-primary">
          + New Run Profile
        </button>
      </div>

      {error && (
        <div className="error-banner">
          {error}
          <button onClick={() => setError(null)} className="error-dismiss">×</button>
        </div>
      )}

      {/* Profiles Grid */}
      <div className="profiles-grid">
        {profiles.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📦</div>
            <h3>No Run Profiles Yet</h3>
            <p>Create a run profile to bundle flows and pipelines together for batch execution.</p>
            <button onClick={() => setShowCreateModal(true)} className="btn-primary">
              Create Your First Profile
            </button>
          </div>
        ) : (
          profiles.map((profile) => (
            <div key={profile.id} className="profile-card">
              <div className="profile-header">
                <div className="profile-title">
                  <h3>{profile.name}</h3>
                  <span className={`badge ${getStatusBadge(profile.status)}`}>
                    {profile.status}
                  </span>
                </div>
                <div className="profile-actions">
                  <button
                    onClick={() => handleTriggerProfile(profile.id)}
                    className="btn-icon btn-run"
                    title="Run Profile"
                  >
                    ▶
                  </button>
                  <button
                    onClick={() => navigate(`/run-profiles/${profile.id}`)}
                    className="btn-icon"
                    title="Edit Profile"
                  >
                    ✏️
                  </button>
                  <button
                    onClick={() => handleDeleteProfile(profile.id)}
                    className="btn-icon btn-danger"
                    title="Delete Profile"
                  >
                    🗑️
                  </button>
                </div>
              </div>

              {profile.description && (
                <p className="profile-description">{profile.description}</p>
              )}

              <div className="profile-stats">
                <div className="stat">
                  <span className="stat-value">{profile.item_count || 0}</span>
                  <span className="stat-label">Items</span>
                </div>
                <div className="stat">
                  <span className="stat-value">{profile.total_runs || 0}</span>
                  <span className="stat-label">Runs</span>
                </div>
                <div className="stat">
                  <span className="stat-value">{profile.success_rate?.toFixed(0) || 0}%</span>
                  <span className="stat-label">Success</span>
                </div>
              </div>

              <div className="profile-meta">
                <div className="meta-item">
                  <span className="meta-label">Last Run:</span>
                  <span className="meta-value">{formatDate(profile.last_run_at)}</span>
                </div>
                <div className="meta-item">
                  <span className="meta-label">Avg Duration:</span>
                  <span className="meta-value">
                    {profile.avg_duration_seconds ? `${Math.round(profile.avg_duration_seconds / 60)}m` : 'N/A'}
                  </span>
                </div>
              </div>

              <div className="profile-options">
                {profile.stop_on_failure && (
                  <span className="option-tag">Stop on Failure</span>
                )}
                {profile.parallel_execution && (
                  <span className="option-tag">Parallel Execution</span>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create Run Profile</h2>
              <button onClick={() => setShowCreateModal(false)} className="modal-close">×</button>
            </div>
            <form onSubmit={handleCreateProfile}>
              <div className="form-group">
                <label htmlFor="name">Profile Name *</label>
                <input
                  id="name"
                  type="text"
                  value={newProfile.name}
                  onChange={(e) => setNewProfile({ ...newProfile, name: e.target.value })}
                  placeholder="e.g., Daily Full Refresh"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  value={newProfile.description}
                  onChange={(e) => setNewProfile({ ...newProfile, description: e.target.value })}
                  placeholder="Describe what this profile does..."
                  rows={3}
                />
              </div>
              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={newProfile.stop_on_failure}
                    onChange={(e) => setNewProfile({ ...newProfile, stop_on_failure: e.target.checked })}
                  />
                  Stop on Failure
                  <span className="checkbox-hint">Stop running remaining items if one fails</span>
                </label>
              </div>
              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={newProfile.parallel_execution}
                    onChange={(e) => setNewProfile({ ...newProfile, parallel_execution: e.target.checked })}
                  />
                  Parallel Execution
                  <span className="checkbox-hint">Run items in parallel where possible</span>
                </label>
              </div>
              <div className="modal-footer">
                <button type="button" onClick={() => setShowCreateModal(false)} className="btn-secondary">
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Create Profile
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default RunProfiles;
